<template>
  <a-modal
    :footer="null"
    :width="600"
    :afterClose="handleAfterClose"
    v-model:visible="visible"
    :maskClosable="false"
    class="j-modal-box-shadow"
    :maskStyle="{ 'background-color': 'inherit' }"
    :title="
      isSpare
        ? $t('add_part_to_cart.modal_title_spare')
        : $t('add_part_to_cart.modal_title')
    "
  >
    <div>
      <a-form
        ref="form"
        :model="form"
        :label-col="labelCol"
        :wrapper-col="wrapperCol"
      >
        <a-form-item :label="$t('add_part_to_cart.photo')">
          <div class="img-container">
            <img alt="photo" v-if="getImg" :src="getImg" />
            <div v-if="getImg" class="enlarge-icon">
              <EnlargeIcon @click.stop="onImgClick" />
            </div>

            <div v-if="!getImg" class="no-img">{{ $t('common.no_img') }}</div>
          </div>
        </a-form-item>

        <a-form-item name="modelNum" :label="$t('add_part_to_cart.model_num')">
          <a-input readOnly v-model:value="form.modelNum" />
        </a-form-item>

        <a-form-item name="partCode" :label="$t('add_part_to_cart.part_code')">
          <a-input readOnly v-model:value="form.partCode" />
        </a-form-item>

        <a-form-item
          name="quantity"
          :label="$t('add_part_to_cart.quantity')"
          :wrapperCol="{ span: 12 }"
          :rules="[
            {
              type: 'string',
              required: true,
              message: $t('add_part_to_cart.quantity_msg')
            },
            validateQuantity()
          ]"
        >
          <a-input
            type="number"
            @change="handleQuantityChange"
            v-model:value="form.quantity"
          >
            <template #addonBefore>
              <MinusOutlined class="btn-icon" @click="substract" />
            </template>
            <template #addonAfter>
              <PlusOutlined class="btn-icon" @click="add" />
            </template>
          </a-input>
        </a-form-item>

        <a-form-item
          :rules="[validateLength()]"
          name="remark"
          :label="$t('add_part_to_cart.remark')"
        >
          <a-textarea v-model:value="form.remark" :rows="4" />
        </a-form-item>

        <div style="text-align: right;">
          <a-button
            :loading="loading"
            @click="handleCancel"
            class="j-outline-btn1 j-height-auto"
          >
            {{ $t('add_part_to_cart.cancel') }}
          </a-button>
          <a-button
            :loading="loading"
            @click="handleConfirm"
            type="primary"
            class="j-height-auto"
            style="margin-left: 10px;"
          >
            {{ $t('add_part_to_cart.confirm') }}
          </a-button>
        </div>
      </a-form>
    </div>

    <PreviewImageModal
      @after-close="handleAfterImgModalClose"
      v-if="showPreviewModal"
      :images="previewImg"
    />
  </a-modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import { message } from 'ant-design-vue';
import numeral from 'numeral';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons-vue';
import EnlargeIcon from '@/components/icons/EnlargeIcon.vue';
import PreviewImageModal from '@/components/preview-image/PreviewImageModal.vue';
import { baseFileUrl } from '@/utils/constants';
import { addToCart } from '@/api/apis';
import { validateQuantity, validateLength } from '@/utils/validate';

interface AddPartToCartModalProps {
  previewImg: string[];
  [key: string]: any;
}

export default defineComponent({
  name: 'AddPartToCartModal',
  emits: ['after-close'],
  props: {
    item: {
      type: Object,
      required: true,
      default: () => {
        return {};
      }
    },
    groupId: {
      type: Number,
      default: null
    },
    vehicleId: {
      type: Number,
      default: null
    },
    isSpare: {
      type: Boolean,
      default: false
    }
  },
  components: {
    EnlargeIcon,
    PreviewImageModal,
    MinusOutlined,
    PlusOutlined
  },
  data(): AddPartToCartModalProps {
    return {
      validateLength,
      validateQuantity,
      visible: true,
      loading: false,
      labelCol: { span: 6 },
      wrapperCol: { span: 18 },
      form: {
        modelNum: this.item.modelNum,
        partCode: this.item.partCode,
        quantity: '1',
        remark: ''
      },
      baseFileUrl,
      showPreviewModal: false,
      previewImg: [],
      isCancel: true
    };
  },
  computed: {
    ...mapState(['userInfo']),
    getImg() {
      let res = '';
      const partsImgs = this.item.partsImgs || [];

      if (partsImgs.length > 0) {
        res = baseFileUrl + partsImgs[0].fileUrl;
      }

      return res;
    }
  },
  methods: {
    handleAfterClose() {
      this.$emit('after-close', this.isCancel);
    },
    handleQuantityChange() {
      const quantity = +this.form.quantity;
      if (quantity > 9999) {
        this.form.quantity = '9999';
      }

      if (quantity === 0) {
        this.form.quantity = '1';
      }
    },
    substract() {
      const quantity = Number(this.form.quantity);
      if (!isNaN(quantity) && quantity > 1) {
        this.form = {
          ...this.form,
          quantity: String(quantity - 1)
        } as any;

        const form = (this.$refs as any).form;

        form.clearValidate('quantity');
      }
    },
    add() {
      const quantity = Number(this.form.quantity || 0);
      if (quantity < 9999) {
        this.form = {
          ...this.form,
          quantity: String(quantity + 1)
        } as any;
        const form = (this.$refs as any).form;
        form.clearValidate('quantity');
      }
    },
    handleCancel() {
      this.visible = false;
    },
    handleConfirm() {
      const form = (this.$refs as any).form;
      form
        .validate()
        .then(async () => {
          this.loading = true;
          const res = await addToCart({
            ...this.form,
            quantity: numeral(this.form.quantity).value(),
            customerId: this.userInfo.id,
            modelNum: this.item.modelNum,
            name: this.item.partName,
            partCode: this.item.partCode,
            groupName: this.item.groupName,
            unit: this.item.unit,
            unitPrice: this.item.sellingPrice,
            consignee: this.userInfo.companyName,
            shoppingType: 2, // 子配件
            groupId: this.groupId,
            vehicleId: this.vehicleId,

            partsId: this.item.id
          });
          if (res.success) {
            this.isCancel = false;
            this.handleCancel();

            message.success(this.$t(res.message));
          }

          this.loading = false;
        })
        .catch();
    },
    onImgClick() {
      this.previewImg.push(
        ...(this.item.partsImgs || [])
          .filter(s => s.fileUrl)
          .map(s => baseFileUrl + s.fileUrl)
      );
      this.showPreviewModal = true;
    },
    handleAfterImgModalClose() {
      this.showPreviewModal = false;

      this.previewImg = [];
    }
  }
});
</script>

<style lang="scss" scoped>
.img-container {
  position: relative;
  .enlarge-icon {
    position: absolute;
    top: 10px;
    left: 136px;
    display: flex;
  }
  img {
    display: block;
    width: 160px;
    height: 100px;
    object-fit: contain;
  }

  .no-img {
    height: 100px;
    width: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
